import React, { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { api, track, setLang as persistLang, ls, isQC, setQC, newQCNonce, localDate } from "@/lib/api";
import { RotateCcw, RefreshCw, X, Volume2, VolumeX } from "lucide-react";
import { audioCtl, isMuted, setMuted } from "@/lib/audio";
import { BreathingRing, SMLogo } from "@/components/UIBits";
import { BallView } from "@/components/BallView";
import { RevealView } from "@/components/RevealView";
import Welcome from "@/screens/Welcome";
import MeetMaidaan from "@/screens/MeetMaidaan";
import PickChase from "@/screens/PickChase";
import Summary from "@/screens/Summary";
import Eliminator from "@/screens/Eliminator";
import HowToPlay from "@/screens/HowToPlay";
import ShareModal from "@/screens/ShareModal";
import { T } from "@/lib/copy";

// B48: mark a persona as played-today so PickChase can badge it. Date-scoped keys auto-expire on local-midnight rollover.
function markPlayed(persona) {
  try { localStorage.setItem(`so_played_${persona}_${localDate()}`, "1"); } catch (e) {}
}

const TRIAL_TONE = { gold: "warm", silver: "warm", weak: "dim", trap: "cold" };

function QCBadge({ onReplay, onRestart, onExit }) {
  return (
    <div className="qc-badge" data-testid="qc-mode-badge">
      <span className="qc-dot" aria-hidden />
      <span className="qc-label">QC</span>
      <button className="qc-btn" onClick={onReplay} data-testid="qc-replay-button"><RotateCcw size={11} /> Replay</button>
      <button className="qc-btn" onClick={onRestart} data-testid="qc-restart-button"><RefreshCw size={11} /> Restart</button>
      <button className="qc-btn qc-exit" onClick={onExit} data-testid="qc-exit-button"><X size={11} /> Exit</button>
    </div>
  );
}

function MuteToggle() {
  const [muted, setMutedLocal] = useState(() => isMuted());
  useEffect(() => {
    const h = () => setMutedLocal(isMuted());
    window.addEventListener("so-mute-change", h);
    return () => window.removeEventListener("so-mute-change", h);
  }, []);
  const toggle = () => { const v = !muted; setMuted(v); setMutedLocal(v); };
  return (
    <button
      onClick={toggle}
      aria-label={muted ? "Unmute Maidaan voice" : "Mute Maidaan voice"}
      title={muted ? "Voice muted — tap to unmute" : "Voice on — tap to mute"}
      data-testid="global-mute-toggle"
      style={{
        position: "fixed", top: 12, right: 12, zIndex: 100,
        width: 34, height: 34, borderRadius: "50%",
        background: "rgba(15,18,20,0.72)",
        border: "1px solid rgba(244,239,230,0.18)",
        color: muted ? "rgba(244,239,230,0.45)" : "var(--sm-blaze)",
        display: "flex", alignItems: "center", justifyContent: "center",
        backdropFilter: "blur(8px)",
        transition: "color 0.2s ease, border-color 0.2s ease",
        cursor: "pointer",
      }}
    >
      {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
    </button>
  );
}

export default function App() {
  const [lang, setLangState] = useState("en"); // V1: English only. V2: restore useState(getLang()).
  const [qc, setQcState] = useState(() => isQC());
  const [phase, setPhase] = useState("boot");
  const [device, setDevice] = useState(null);
  const [personas, setPersonas] = useState({});
  const [persona, setPersona] = useState(null);
  const [beats, setBeats] = useState([]);
  const [meetOrigin, setMeetOrigin] = useState("welcome"); // where MeetMaidaan was opened FROM (Welcome vs PickChase)
  const [meetSeen, setMeetSeen] = useState(() => { try { return localStorage.getItem("so_meet_seen") === "1"; } catch (e) { return false; } });
  const [challenger, setChallenger] = useState(null);

  // knock-up
  const [trials, setTrials] = useState([]);
  const [trialIdx, setTrialIdx] = useState(0);
  const [trialRes, setTrialRes] = useState(null);
  const [trialReveal, setTrialReveal] = useState(false);

  // over
  const [overInfo, setOverInfo] = useState(null); // {over_id, chase, timer_ms}
  const [overState, setOverState] = useState(null); // {score,wickets,idx,grid,freeHitNext}
  const [ballNo, setBallNo] = useState(1);
  const [ball, setBall] = useState(null);
  const [res, setRes] = useState(null);
  const [summary, setSummary] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reviewUsed, setReviewUsed] = useState(false); // DRS: one review per over
  const [showHowTo, setShowHowTo] = useState(false); // gated: opens only via "How to play" tap (no auto-intro)
  function closeHowTo() { try { localStorage.setItem("so_seen_howto", "1"); } catch (e) {} setShowHowTo(false); }

  const t = T(lang);
  const bootedRef = useRef(false);

  const setLang = (l) => {
    setLangState(l); persistLang(l);
    // If the player is mid-slideshow, re-fetch beats in the new language so the caption + audio switch cleanly.
    if (phase === "meet" && persona) {
      api.slideshow(l, persona).then((r) => setBeats(r.beats || [])).catch(() => {});
    }
  };

  // ---- HARD RULE: kill any audio the instant the screen changes ----
  // (root cause of the Maidaan "talking onto the next/difficulty screen")
  useEffect(() => { audioCtl.stop(); }, [phase]);

  // ---- in-app back navigation (no more being trapped in Meet-the-Maidaan) ----
  function goBack(target) {
    audioCtl.stop();
    setPhase(target);
  }

  // ---- boot (run exactly once; StrictMode-safe so a 2nd boot can't clobber navigation) ----
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return; refreshing = true; window.location.reload();
      });
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((reg) => { try { reg.update(); } catch (e) {} }).catch(() => {});
    }
    if (bootedRef.current) return;
    bootedRef.current = true;
    boot();
    // eslint-disable-next-line
  }, []);

  async function boot() {
    // ---- QC mode toggles: ?qc_mode=true|false  ·  ?qc_reset=1 (full first-run) ----
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.has("qc_mode")) {
        const on = sp.get("qc_mode") === "true" || sp.get("qc_mode") === "1";
        setQC(on); setQcState(on);
      }
      if (sp.get("qc_reset") === "1") {
        ["so_onboarded", "so_persona", "so_seen_howto", "so_meet_seen"].forEach((k) => { try { localStorage.removeItem(k); } catch (e) {} });
        // B48: also clear all played-today badges for QC purity.
        try { Object.keys(localStorage).filter((k) => k.startsWith("so_played_")).forEach((k) => localStorage.removeItem(k)); } catch (e) {}
        newQCNonce(); setShowHowTo(true);
      }
    } catch (e) {}
    const qcOn = isQC();

    let chal = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const tok = params.get("c");
      if (tok) {
        // Bug 1: strip ?c= from URL bar as soon as token is captured (same tick, before await) →
        // manual browser shares look clean AND invalid tokens don't linger in the address bar.
        try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
        chal = await api.shareGet(tok); setChallenger(chal);
      }
    } catch (e) {}

    let dev = null;
    try { dev = await api.deviceInit(); setDevice(dev); setPersonas(dev.personas || {}); } catch (e) {}
    if (!dev) { setPhase("welcome"); return; }

    // QC: always start from the top so every flow can be validated end-to-end.
    if (qcOn && !chal) { setPhase("welcome"); return; }

    const onboarded = ls.get("so_onboarded") === "1";
    let p = chal && chal.persona ? chal.persona : ls.get("so_persona", null);

    if (onboarded && p) {
      setPersona(p);
      await resumeOrStart(p, chal);
    } else if (onboarded && !p) {
      setPhase("pick");
    } else {
      setPhase("welcome");
    }
  }

  // returning user: jump straight to today's over (the §1C internalised trigger)
  async function resumeOrStart(p, chal) {
    try {
      const ov = await api.overToday(p, lang);
      if (ov.complete) {
        const s = await api.summary(ov.over_id, lang);
        setSummary({ ...s }); setOverInfo({ over_id: ov.over_id, chase: ov.chase, timer_ms: ov.timer_ms });
        if (chal) setChallenger(chal);
        markPlayed(p); // B48
        setPhase("summary");
        return;
      }
      // resume / start: straight into the over
      setOverInfo({ over_id: ov.over_id, chase: ov.chase, timer_ms: ov.timer_ms });
      setOverState({ score: ov.score, wickets: ov.wickets, idx: ov.idx, grid: [], freeHitNext: ov.free_hit_next });
      setReviewUsed(false);
      track("over_ready", { persona: p, over_id: ov.over_id });
      await loadBall(ov.over_id, ov.idx + 1);
      setPhase("play");
    } catch (e) { setPhase("pick"); }
  }

  // ---- welcome ----
  async function onStart(name) {
    setBusy(true);
    try { const d = await api.deviceInit((name || "").trim()); setDevice(d); setPersonas(d.personas || {}); } catch (e) {}
    track("onboard_started");
    setBusy(false);
    setPhase("pick");
  }
  async function openMeet() {
    setMeetOrigin(phase);
    try { localStorage.setItem("so_meet_seen", "1"); setMeetSeen(true); } catch (e) {}
    try { const r = await api.slideshow(lang, persona); setBeats(r.beats || []); } catch (e) {}
    setPhase("meet");
  }

  // ---- pick ----
  async function onPick(key) {
    if (isQC()) newQCNonce(); // QC: fresh over id per game -> endless replay
    setPersona(key);
    ls.set("so_persona", key);
    track("persona_picked", { persona: key });
    const onboarded = ls.get("so_onboarded") === "1";
    if (!onboarded) {
      try { const r = await api.knockup(key); setTrials(r.balls || []); setTrialIdx(0); setTrialReveal(false); setPhase("knockup"); } catch (e) { startOver(key); }
    } else {
      startOver(key);
    }
  }

  // ---- knock-up ----
  async function onTrialLock(optionIndex) {
    // No decision / time-out in the warm-up is a DOT — never a scoring option (was sending option 0 -> could be a six).
    if (optionIndex == null || optionIndex < 0) {
      setTrialRes({
        outcome: "0", reveal: (t.trialTimeout[persona] || t.trialTimeout.casual), tier: "weak", runs: 0, is_wicket: false,
        free_hit_note: null, timed_out: true, is_scout: false, principle: "", why: "", seat: null,
      });
      setTrialReveal(true);
      return;
    }
    try {
      const tb = trials[trialIdx];
      const r = await api.knockAnswer(tb.id, optionIndex, lang);
      setTrialRes({
        outcome: r.outcome, reveal: r.reveal, tier: r.tier, runs: 0, is_wicket: false,
        free_hit_note: null, timed_out: false, is_scout: false, principle: "", why: "", seat: null,
      });
      setTrialReveal(true);
    } catch (e) { nextTrial(); }
  }
  function nextTrial() {
    setTrialReveal(false);
    if (trialIdx + 1 < trials.length) { setTrialIdx(trialIdx + 1); }
    else { ls.set("so_onboarded", "1"); startOver(persona); }
  }

  // ---- start today's over ----
  async function startOver(p) {
    setBusy(true);
    try {
      const ov = await api.overToday(p, lang);
      setOverInfo({ over_id: ov.over_id, chase: ov.chase, timer_ms: ov.timer_ms });
      if (ov.complete) {
        const s = await api.summary(ov.over_id, lang); setSummary(s); markPlayed(p); setPhase("summary"); setBusy(false); return; // B48
      }
      setOverState({ score: ov.score, wickets: ov.wickets, idx: ov.idx, grid: [], freeHitNext: ov.free_hit_next });
      setReviewUsed(false); // fresh over → DRS available again
      track("over_ready", { persona: p, over_id: ov.over_id });
      await loadBall(ov.over_id, ov.idx + 1);
      setPhase("play");
    } catch (e) {}
    setBusy(false);
  }

  async function loadBall(overId, n) {
    setBusy(true);
    try {
      const r = await api.getBall(overId, n);
      setBall(r.ball); setBallNo(n);
    } catch (e) {}
    setBusy(false);
  }

  // ---- play / answer ----
  async function onLock(optionIndex, ms) {
    try {
      const r = await api.answer(overInfo.over_id, ballNo, optionIndex, ms, lang);
      setRes(r);
      setOverState((s) => ({
        score: r.score, wickets: r.wickets, idx: (s ? s.idx : 0) + 1,
        grid: [...(s ? s.grid : []), r.outcome], freeHitNext: r.free_hit_next,
      }));
      setPhase("reveal");
    } catch (e) {}
  }

  async function onRevealNext() {
    audioCtl.stop();
    if (res && res.complete) {
      const s = res.summary || (await api.summary(overInfo.over_id, lang).catch(() => null));
      setSummary(s); markPlayed(persona); setPhase("summary"); // B48
    } else {
      await loadBall(overInfo.over_id, ballNo + 1);
      setPhase("play");
    }
  }

  // ---- summary ----
  function onPlayAgain() {
    audioCtl.stop();
    setRes(null); setOverState(null); setBall(null); setSummary(null);
    setPhase("welcome"); // B48: return to Welcome (returning-user greeting), not bare Pick. Rollback: setPhase("pick").
  }
  async function onElimDone(result) {
    audioCtl.stop();
    try { const s = await api.summary(overInfo.over_id, lang); setSummary(s); } catch (e) {}
    setPhase("summary");
  }
  const createShare = useCallback(async () => {
    try { return await api.shareCreate(overInfo.over_id); } catch (e) { return null; }
  }, [overInfo]);

  // ---- render ----
  const Loading = (
    <div className="so-pad-top flex-1 flex items-center justify-center" style={{ minHeight: "60dvh" }}>
      <BreathingRing label={t.tagline} />
    </div>
  );

  return (
    <div className="so-stage">
      <div className={"so-frame" + (phase === "reveal" && res && res.is_wicket ? " is-cold" : "")}>
        {qc && (
          <QCBadge
            onReplay={() => { audioCtl.stop(); newQCNonce(); setRes(null); setOverState(null); setBall(null); setSummary(null); setChallenger(null); setPhase("pick"); }}
            onRestart={() => { const base = window.location.origin + window.location.pathname; window.location.href = base + "?qc_mode=true&qc_reset=1"; }}
            onExit={() => { setQC(false); window.location.href = window.location.origin + window.location.pathname; }}
          />
        )}
        <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: "#16233F", color: "#F4EFE6", border: "1px solid rgba(244,239,230,0.12)" } }} />
        {!["play", "reveal", "boot"].includes(phase) && <MuteToggle />}
        <div className="so-scroll">
          {!["play", "reveal", "boot", "welcome"].includes(phase) && (
            <div className="global-topbar" data-testid="global-topbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px 2px" }}>
              <SMLogo height={22} opacity={0.75} />
              {/* V2: <LangToggle lang={lang} setLang={setLang} /> — Desi deferred to V2. */}
            </div>
          )}
          {/* B47: Knockup MAIDAAN? pill removed — Meet is now a Welcome-CTA mandate (meetSeen always true here). */}
          {phase === "boot" && Loading}

          {phase === "welcome" && (
            <Welcome lang={lang} setLang={setLang} onStart={onStart} onMeet={openMeet} onHowTo={() => setShowHowTo(true)} challenger={challenger} streak={device && device.streak ? device.streak : 0} meetSeen={meetSeen} />
          )}

          {phase === "meet" && (
            <MeetMaidaan lang={lang} beats={beats} onDone={() => setPhase("pick")} onBack={() => goBack(meetOrigin || "welcome")} />
          )}

          {phase === "pick" && (
            <PickChase lang={lang} personas={personas} onPick={onPick} onMeet={openMeet} challenger={challenger} meetSeen={meetSeen} />
          )}

          {phase === "knockup" && trials[trialIdx] && !trialReveal && (
            <BallView
              key={"trial-" + trials[trialIdx].id}
              ball={trials[trialIdx]}
              timerMs={40000}
              trial={{ n: trialIdx + 1, total: trials.length }}
              onLock={(oi) => onTrialLock(oi)}
              lang={lang}
            />
          )}
          {phase === "knockup" && trialReveal && trialRes && (
            <RevealView res={trialRes} overId={overInfo ? overInfo.over_id : "trial"} ballNo={0} trial lang={lang}
              persona={persona}
              onNext={nextTrial} finishLabel={t.next} />
          )}

          {phase === "play" && ball && overState && overInfo && (
            <BallView
              key={"ball-" + ballNo}
              ball={ball}
              timerMs={overInfo.timer_ms}
              freeHit={ball.free_hit}
              scoreState={{
                score: overState.score, runsNeeded: Math.max(0, overInfo.chase - overState.score),
                ballsLeft: 6 - overState.idx, wickets: overState.wickets, idx: overState.idx, grid: overState.grid,
              }}
              onLock={onLock}
              lang={lang}
            />
          )}

          {phase === "reveal" && res && (
            <RevealView res={res} overId={overInfo.over_id} ballNo={ballNo} lang={lang}
              persona={persona}
              reviewUsed={reviewUsed} onReviewUsed={() => setReviewUsed(true)}
              onNext={onRevealNext} finishLabel={res.complete ? t.finishBtn : t.nextBall} />
          )}

          {phase === "summary" && summary && (
            <Summary lang={lang} summary={summary} persona={persona} overId={overInfo.over_id}
              onPlayAgain={onPlayAgain} onShare={() => setShareOpen(true)}
              onPlayEliminator={() => setPhase("eliminator")} challenger={challenger} />
          )}

          {phase === "eliminator" && overInfo && (
            <Eliminator lang={lang} overId={overInfo.over_id} persona={persona} onDone={onElimDone} />
          )}

          {(busy && ["play", "reveal"].indexOf(phase) === -1) && phase !== "boot" && null}
        </div>

        <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} summary={summary || {}}
          persona={persona} lang={lang} createShare={createShare} />

        {showHowTo && <HowToPlay lang={lang} onClose={closeHowTo} />}
      </div>
    </div>
  );
}
